import React, { useState } from 'react';
import {
  FaTwitter,
  FaLinkedin,
  FaFacebook,
  FaLink,
  FaCheck,
  FaShareAlt,
} from 'react-icons/fa';

interface SocialShareProps {
  url: string;
  title: string;
  description?: string;
  docId?: string;
}

const SocialShare: React.FC<SocialShareProps> = ({ url, title, docId }) => {
  const [copied, setCopied] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const API_URL = import.meta.env.VITE_API_BACKEND_URL;

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const trackShare = async (platform: string) => {
    if (!docId) return;

    try {
      await fetch(`${API_URL}/documentation/dev-docs/${docId}/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ platform }),
      });
    } catch (err) {
      console.error('Failed to track share:', err);
    }
  };

  const handleShare = (platform: string) => {
    window.open(shareLinks[platform as keyof typeof shareLinks], '_blank', 'width=600,height=400');
    trackShare(platform);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="flex items-center gap-2 px-4 py-2 backdrop-blur-xl bg-white/60 border border-white/40 rounded-full text-gray-700 hover:bg-gradient-to-r hover:from-Color hover:to-brightColor hover:text-white transition-all duration-300 font-medium hover:scale-105"
      >
        <FaShareAlt />
        <span>Share</span>
      </button>

      {showMenu && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowMenu(false)}
          ></div>

          {/* Share Menu */}
          <div className="absolute right-0 mt-2 z-50 backdrop-blur-xl bg-white/90 rounded-2xl shadow-2xl border border-white/40 p-4 min-w-[240px]">
            <h4 className="text-sm font-bold text-gray-900 mb-3">Share this documentation</h4>
            <div className="space-y-2">
              <button
                onClick={() => handleShare('twitter')}
                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl bg-white/60 hover:bg-gradient-to-r hover:from-Color hover:to-brightColor hover:text-white transition-all duration-300 group"
              >
                <FaTwitter className="text-lg" />
                <span className="font-medium">Twitter</span>
              </button>

              <button
                onClick={() => handleShare('linkedin')}
                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl bg-white/60 hover:bg-gradient-to-r hover:from-Color hover:to-brightColor hover:text-white transition-all duration-300 group"
              >
                <FaLinkedin className="text-lg" />
                <span className="font-medium">LinkedIn</span>
              </button>

              <button
                onClick={() => handleShare('facebook')}
                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl bg-white/60 hover:bg-gradient-to-r hover:from-Color hover:to-brightColor hover:text-white transition-all duration-300 group"
              >
                <FaFacebook className="text-lg" />
                <span className="font-medium">Facebook</span>
              </button>

              <button
                onClick={copyToClipboard}
                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl bg-white/60 hover:bg-gradient-to-r hover:from-Color hover:to-brightColor hover:text-white transition-all duration-300 group"
              >
                {copied ? <FaCheck className="text-lg" /> : <FaLink className="text-lg" />}
                <span className="font-medium">{copied ? 'Copied!' : 'Copy Link'}</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SocialShare;
